import React, { useState, useEffect } from 'react';
import Modal from '../components/Modal';
import { userService } from '../services/userService';
import { roleService } from '../services/roleService';
import { User, Role, UserUpdateDto } from '../types/types';
import { Users, Edit2, Trash2, Search, Shield } from 'lucide-react';
import './UserManagement.css';

const UserManagement: React.FC = () => {
    const [users, setUsers] = useState<User[]>([]);
    const [roles, setRoles] = useState<Role[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [editFormData, setEditFormData] = useState<UserUpdateDto>({
        username: '',
        password: '',
        role: '',
    });

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const [usersData, rolesData] = await Promise.all([
                userService.getAllUsers(),
                roleService.getAllRoles(),
            ]);
            setUsers(usersData);
            setRoles(rolesData);
        } catch (error) {
            console.error('Error loading data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (user: User) => {
        setSelectedUser(user);
        setEditFormData({
            username: user.username,
            password: '',
            role: user.role.name,
        });
        setEditModalOpen(true);
    };

    const handleDelete = (user: User) => {
        setSelectedUser(user);
        setDeleteModalOpen(true);
    };

    const confirmEdit = async () => {
        if (!selectedUser) return;

        try {
            await userService.updateUser(selectedUser.id, editFormData);
            await loadData();
            setEditModalOpen(false);
            setSelectedUser(null);
        } catch (error) {
            console.error('Error updating user:', error);
            alert('Error al actualizar el usuario');
        }
    };

    const confirmDelete = async () => {
        if (!selectedUser) return;

        try {
            await userService.deleteUser(selectedUser.id);
            await loadData();
            setDeleteModalOpen(false);
            setSelectedUser(null);
        } catch (error) {
            console.error('Error deleting user:', error);
            alert('Error al eliminar el usuario');
        }
    };

    const filteredUsers = users.filter((user) =>
        user.username.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="user-management-container">
                <div className="page-header">
                    <div>
                        <h1>Gestión de Usuarios</h1>
                        <p className="page-subtitle">Administrar usuarios y roles del sistema</p>
                    </div>
                    <Users size={32} className="header-icon" />
                </div>

                <div className="card">
                    <div className="table-controls">
                        <div className="search-box">
                            <Search size={20} />
                            <input
                                type="text"
                                placeholder="Buscar por nombre de usuario..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="search-input"
                            />
                        </div>
                    </div>

                    {loading ? (
                        <div className="loading-state">Cargando usuarios...</div>
                    ) : (
                        <div className="table-container">
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Usuario</th>
                                        <th>Rol</th>
                                        <th>Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredUsers.map((user) => (
                                        <tr key={user.id}>
                                            <td>{user.id}</td>
                                            <td>
                                                <div className="user-cell">
                                                    <span className="username">{user.username}</span>
                                                </div>
                                            </td>
                                            <td>
                                                <span className={`role-badge role-${user.role.name.toLowerCase()}`}>
                                                    <Shield size={14} />
                                                    {user.role.name}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="action-buttons">
                                                    <button
                                                        className="btn-icon btn-edit"
                                                        onClick={() => handleEdit(user)}
                                                        title="Editar usuario"
                                                    >
                                                        <Edit2 size={18} />
                                                    </button>
                                                    <button
                                                        className="btn-icon btn-delete"
                                                        onClick={() => handleDelete(user)}
                                                        title="Eliminar usuario"
                                                    >
                                                        <Trash2 size={18} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {filteredUsers.length === 0 && (
                                <div className="empty-state">No se encontraron usuarios</div>
                            )}
                        </div>
                    )}
                </div>

                {/* Edit Modal */}
                <Modal
                    isOpen={editModalOpen}
                    onClose={() => setEditModalOpen(false)}
                    title="Editar Usuario"
                    size="medium"
                >
                    <div className="modal-form">
                        <div className="form-group">
                            <label htmlFor="edit-username">Nombre de Usuario</label>
                            <input
                                type="text"
                                id="edit-username"
                                value={editFormData.username}
                                onChange={(e) =>
                                    setEditFormData({ ...editFormData, username: e.target.value })
                                }
                                className="form-input"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="edit-password">Nueva Contraseña (opcional)</label>
                            <input
                                type="password"
                                id="edit-password"
                                value={editFormData.password}
                                onChange={(e) =>
                                    setEditFormData({ ...editFormData, password: e.target.value })
                                }
                                placeholder="Dejar en blanco para mantener la actual"
                                className="form-input"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="edit-role">Rol</label>
                            <select
                                id="edit-role"
                                value={editFormData.role}
                                onChange={(e) => setEditFormData({ ...editFormData, role: e.target.value })}
                                className="form-select"
                            >
                                {roles.map((role) => (
                                    <option key={role.id} value={role.name}>
                                        {role.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="modal-actions">
                            <button className="btn btn-outline" onClick={() => setEditModalOpen(false)}>
                                Cancelar
                            </button>
                            <button className="btn btn-primary" onClick={confirmEdit}>
                                Guardar Cambios
                            </button>
                        </div>
                    </div>
                </Modal>

                {/* Delete Confirmation Modal */}
                <Modal
                    isOpen={deleteModalOpen}
                    onClose={() => setDeleteModalOpen(false)}
                    title="Confirmar Eliminación"
                    size="small"
                >
                    <div className="delete-confirmation">
                        <p>
                            ¿Está seguro que desea eliminar al usuario{' '}
                            <strong>{selectedUser?.username}</strong>?
                        </p>
                        <p className="warning-text">Esta acción no se puede deshacer.</p>

                        <div className="modal-actions">
                            <button className="btn btn-outline" onClick={() => setDeleteModalOpen(false)}>
                                Cancelar
                            </button>
                            <button className="btn btn-danger" onClick={confirmDelete}>
                                Eliminar
                            </button>
                        </div>
                    </div>
                </Modal>
            </div>
    );
};

export default UserManagement;
